import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/seo/SEO';
import blogService from '../../services/blog';
import { useLanguageLink } from '../../hooks/useLanguageLink';

export default function BlogPostPage() {
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  const currentLang = lang || 'en';
  const { t } = useTranslation('blog');
  const { createLink } = useLanguageLink();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog-post', slug, currentLang],
    queryFn: () => blogService.getPublicBlogPost(slug!, currentLang),
    enabled: !!slug,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Post not found</h1>
          <a href={createLink('/blog')} className="text-blue-400 hover:text-blue-300">
            ← {t('backToBlog', 'Back to Blog')}
          </a>
        </div>
      </div>
    );
  }

  const publishedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <>
      <SEO
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        keywords={post.meta_keywords || undefined}
        type="article"
      />

      <div className="min-h-screen bg-gray-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-8">
            <a
              href={createLink('/blog')}
              className="inline-flex items-center text-blue-400 hover:text-blue-300 text-sm transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('backToBlog', 'Back to Blog')}
            </a>
          </div>

          <article>
            <header className="mb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-xs font-medium uppercase tracking-wide">
                  {post.category}
                </span>
                {post.read_time && (
                  <span className="text-gray-500 text-sm">{post.read_time} min read</span>
                )}
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                {post.title}
              </h1>

              <p className="text-xl text-gray-400 leading-relaxed mb-6">{post.excerpt}</p>

              <div className="flex items-center gap-4 text-sm text-gray-500 border-t border-gray-800 pt-6">
                {post.author && <span className="font-medium text-gray-400">{post.author}</span>}
                {publishedDate && <span>{publishedDate}</span>}
              </div>
            </header>

            {post.image_url && (
              <div className="mb-12 rounded-2xl overflow-hidden">
                <img src={post.image_url} alt={post.title} className="w-full object-cover" />
              </div>
            )}

            <div
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-white prose-headings:font-bold
                prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-gray-300 prose-p:leading-relaxed
                prose-a:text-blue-400 prose-a:no-underline hover:prose-a:text-blue-300
                prose-strong:text-white
                prose-ul:text-gray-300 prose-ol:text-gray-300
                prose-li:my-1
                prose-blockquote:border-blue-500 prose-blockquote:text-gray-400
                prose-code:text-blue-300 prose-code:bg-gray-800 prose-code:px-1 prose-code:rounded
                prose-pre:bg-gray-800 prose-pre:border prose-pre:border-gray-700"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </article>
        </div>
      </div>
    </>
  );
}
